import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import { Button } from "components/common/Button";
import { SearchBar } from "components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { MetaAddButton } from "components/main/datasets/DatasetMetaSection";
import { ListEmptyShell } from "components/main/overview/ListEmptyShell";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { createLogger } from "debug/DebugingLogger";
import { useOrgContextStore } from "components/main/stores/orgContextStore";
import {
  selectByHostFragment,
  useConnectionsStore,
} from "stores/connectionsStore";
import type { SavedConnection } from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { ConnectionRow } from "./ConnectionRow";
import { ConnectionEditModal } from "./ConnectionEditModal";
import { NewConnectionPanel } from "./NewConnectionPanel";
import {
  ColumnTopSpacer,
  ListBox,
  ListCount,
} from "components/main/pages/pageColumn";
import { plural } from "components/common/setUtils";

const log = createLogger("ConnectionsPage");

/// Main-app Connections page. Mirrors the Overview / Datasets shell: a
/// centered column with a fixed filter bar and a scrolling list whose section
/// header sticks. Rows are MRU-sorted; the "+ Add connection" pill lands in the
/// same trailing spot as those pages' add affordances.
export const ConnectionsPage: React.FC = () => {
  const list = useConnectionsStore((s) => s.list);
  const loadError = useConnectionsStore((s) => s.loadError);
  const fetchIfStale = useConnectionsStore((s) => s.fetchIfStale);
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const refetch = useConnectionsStore((s) => s.refetch);
  const remove = useConnectionsStore((s) => s.remove);
  const checkHealthIfStale = useConnectionsStore((s) => s.checkHealthIfStale);

  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const highlightId = searchParams.get("highlight");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [newPanelOpen, setNewPanelOpen] = useState(false);
  const [editing, setEditing] = useState<SavedConnection | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SavedConnection | null>(
    null,
  );
  const [deletePending, setDeletePending] = useState(false);

  useEffect(() => {
    fetchIfStale({ orgId: activeOrgId }).catch((e) =>
      log.error("fetchIfStale failed: %o", e),
    );
  }, [fetchIfStale, activeOrgId]);

  // Re-runs when the list changes (load, create, update — update busts
  // the health entry, so the changed connection re-probes live).
  useEffect(() => {
    if (list === null) return;
    checkHealthIfStale(list).catch((e) =>
      log.error("checkHealthIfStale failed: %o", e),
    );
  }, [list, checkHealthIfStale]);

  useEffect(() => {
    if (editId === null) {
      if (editing !== null) setEditing(null);
      return;
    }
    if (list === null) return;
    const match = list.find((c) => c.id === editId);
    if (match) {
      setEditing(match);
    } else {
      showNotificationGlobal("That connection no longer exists.", "warning");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("edit");
          return next;
        },
        { replace: true },
      );
      setEditing(null);
    }
  }, [editId, list]);

  useEffect(() => {
    if (highlightId === null || list === null) return;
    const match = list.find((c) => c.id === highlightId);
    if (!match) {
      showNotificationGlobal("That connection no longer exists.", "warning");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("highlight");
          return next;
        },
        { replace: true },
      );
      return;
    }
    setHighlightedId(match.id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("highlight");
        return next;
      },
      { replace: true },
    );
    const scrollTimer = window.setTimeout(() => {
      document
        .getElementById(`connection-${match.id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
    const clearTimer = window.setTimeout(() => setHighlightedId(null), 1200);
    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
    // setSearchParams is stable per react-router; omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, list]);

  const filteredSorted = useMemo(() => {
    if (list === null) return [];
    const filtered = search ? selectByHostFragment(list, search) : list;
    // MRU sort — falls back to created_at when never used.
    return [...filtered].sort((a, b) => {
      const ak = a.last_used_at ?? a.created_at;
      const bk = b.last_used_at ?? b.created_at;
      return bk - ak;
    });
  }, [list, search]);

  const openEditModal = (connection: SavedConnection) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("edit", connection.id);
        return next;
      },
      { replace: true },
    );
  };

  const closeEditModal = () => {
    setEditing(null);
    if (editId !== null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("edit");
          return next;
        },
        { replace: true },
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeletePending(true);
    try {
      await remove(confirmDelete.id);
      showNotificationGlobal("Connection deleted.", "success");
      setConfirmDelete(null);
    } catch (e) {
      log.error("delete failed: %o", e);
      showNotificationGlobal(
        e instanceof Error ? e.message : "Failed to delete connection",
        "error",
      );
    } finally {
      setDeletePending(false);
    }
  };

  const total = list?.length ?? 0;
  const noMatches = total > 0 && filteredSorted.length === 0;

  return (
    <div className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6">
      <ColumnTopSpacer />
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "45rem",
          paddingBottom: "2.5rem",
        }}
      >
        {total === 0 ? (
          <ListEmptyShell
            icon={<StorageOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Connections"
            hint="Add a connection to import tables from external databases."
            loading={list === null}
            addLabel="Add connection"
            onAdd={() => setNewPanelOpen(true)}
            search={{
              value: search,
              onChange: setSearch,
              placeholder: "Search by name or host",
            }}
          />
        ) : (
          <>
            {/* Fixed: the filter bar stays put while the list below scrolls. */}
            <div className="shrink-0 mb-6">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search by name or host"
                size="lg"
              />
            </div>

            <ListBox>
              {loadError && (
                <ErrorBanner
                  message={loadError}
                  onRetry={() => {
                    void refetch();
                  }}
                />
              )}

              <div className="mb-8">
                <div className="sticky top-0 z-10 bg-bg overflow-hidden">
                  <SectionRule
                    inset
                    icon={<StorageOutlinedIcon sx={{ fontSize: 14 }} />}
                    label="Connections"
                  />
                </div>

                {noMatches ? (
                  <div className="text-center py-12 text-small text-fg-muted italic">
                    No connections match “{search.trim()}”.
                  </div>
                ) : (
                  filteredSorted.map((c) => (
                    <ConnectionRow
                      key={c.id}
                      connection={c}
                      onEdit={openEditModal}
                      onDelete={setConfirmDelete}
                      highlighted={c.id === highlightedId}
                    />
                  ))
                )}
              </div>
            </ListBox>

            {/* Pinned below the scroll: the count + Add connection pill stay
                visible while the list scrolls, and the pulse ring is never
                clipped by the scroll container's overflow. */}
            <div className="shrink-0 flex items-center mt-6">
              <ListCount parts={[plural(total, "connection")]} />
              <span className="ml-auto">
                <MetaAddButton
                  label="Add connection"
                  onClick={() => setNewPanelOpen(true)}
                />
              </span>
            </div>
          </>
        )}
      </div>
      <NewConnectionPanel
        open={newPanelOpen}
        onClose={() => setNewPanelOpen(false)}
        onCreated={() => {
          showNotificationGlobal("Connection saved.", "success");
        }}
      />
      <ConnectionEditModal
        open={editing !== null}
        connection={editing}
        onClose={closeEditModal}
        onSaved={() => {
          showNotificationGlobal("Connection updated.", "success");
        }}
      />
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete connection?"
        message={
          confirmDelete
            ? `Delete "${confirmDelete.display_name}"? Any imported tables backed by this connection will fail to sync until you add another connection that matches host:port/database.`
            : ""
        }
        confirmText="Delete"
        confirmVariant="destructive"
        isLoading={deletePending}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

// ============================================================================
// Sub-components (page-local — not reused elsewhere)
// ============================================================================

const ErrorBanner: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="px-3 py-2 mb-4 bg-error/10 border border-error/30 rounded-app flex items-center justify-between">
    <span className="text-fg-muted-regular-small text-error">
      Could not load connections — {message}
    </span>
    <Button variant="secondary" size="xs" onClick={onRetry}>
      Retry
    </Button>
  </div>
);
